/**
 * Distance Calculation Utilities
 *
 * Client-side utilities for calculating and formatting distances
 * Uses Haversine formula - pure math, no API required
 */

/**
 * Calculate distance between two coordinates using Haversine formula
 *
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lng1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lng2 - Longitude of point 2
 * @param {string} unit - 'miles' or 'km' (default: 'miles')
 * @returns {number|null} - Distance in specified unit, or null if invalid coords
 */
export const calculateDistance = (lat1, lng1, lat2, lng2, unit = 'miles') => {
  if (
    lat1 === null || lat1 === undefined ||
    lng1 === null || lng1 === undefined ||
    lat2 === null || lat2 === undefined ||
    lng2 === null || lng2 === undefined
  ) {
    return null;
  }

  const R = unit === 'km' ? 6371 : 3959; // Earth's radius in km or miles

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Convert degrees to radians
 */
const toRad = (deg) => deg * (Math.PI / 180);

/**
 * Format distance for display
 *
 * @param {number} distance - Distance in miles
 * @returns {string|null} - Formatted string like "3.2 mi" or "15 mi"
 */
export const formatDistance = (distance) => {
  if (distance === null || distance === undefined) {
    return null;
  }

  if (distance < 0.1) {
    return '< 0.1 mi';
  }

  if (distance < 10) {
    return `${distance.toFixed(1)} mi`;
  }

  return `${Math.round(distance)} mi`;
};

/**
 * Get a readable distance description
 *
 * @param {number} distance - Distance in miles
 * @returns {string|null} - Description like "Very close (1.2 mi)"
 */
export const getDistanceDescription = (distance) => {
  if (distance === null || distance === undefined) {
    return null;
  }

  const formatted = formatDistance(distance);

  if (distance < 1) {
    return `Very close (${formatted})`;
  }

  if (distance < 5) {
    return `Nearby (${formatted})`;
  }

  if (distance < 15) {
    return `${formatted} away`;
  }

  if (distance < 30) {
    return `${formatted} away`;
  }

  return `${formatted} away`;
};

/**
 * Check if a distance is within a reasonable pickup range
 *
 * @param {number} distance - Distance in miles
 * @param {number} maxDistance - Maximum reasonable distance (default: 50 miles)
 * @returns {boolean}
 */
export const isWithinPickupRange = (distance, maxDistance = 50) => {
  if (distance === null || distance === undefined) {
    return false;
  }
  return distance <= maxDistance;
};

export default {
  calculateDistance,
  formatDistance,
  getDistanceDescription,
  isWithinPickupRange
};
